import React, { FC } from 'react';
import classnames from 'classnames';
import { history, useLocation } from 'umi';
// @ts-ignore
import * as rbd from 'react-beautiful-dnd';
import OverviewCard from './component/OverviewCard';
import ProjectRow from './component/ProjectRow';
import { TYPES } from '@sg/pages/StudioManagement/InDevProjects/pages/InDevProjectsAll/const';
import './index.scss';
import CardCommon from '@sg/components/CardCommon';
import NoData from '@/pages/intelligence/components/NoData';
import { InDevProjectsTab, createBtnPoint, SGBuriedBtnPoint } from '@sg/utils/buriedPoint';

interface IProps {
  studio?: any;
  selectedMonth?: any;
  isEditing?: boolean;
  handleDateChange?: (value: any) => void;
  handleConfirm?: () => void;
  handleProjectsReorder?: (projects: any[]) => void;
  handleProjectsReorderCancel?: () => void;
  handleEditNotes?: () => void;
  setIsEditingNotes?: (value: boolean) => void;
  loading?: boolean;
  editable?: boolean;
  cascadeTreeOptions?: any[];
  allCascadeValues?: string[];
  cascadeInfoList?: any[];
  projectListAll?: any[];
  projects?: any[];
  /** GameSelectAll 加载到的 studio/game 列表，用于点击 project 图标时映射 unified_id */
  studioList?: any[];
}

const InDevProjectsOverview: FC<IProps> = (props: any) => {
  const { selectedMonth, isEditing, handleProjectsReorder, projects, studioList, projectListAll } = props;

  const location = useLocation();

  // 与 GameSelectAll.handleImageClick 行为对齐：
  // 把 URL 上的 project_id 切换为目标 project 对应的 unified_id，
  // 并 push 到 /strategic/home，触发父组件 RoutePrompt 拦截及 studio 状态同步。
  const handleProjectIconClick = (project: any) => {
    if (!project?.project_id) {
      return;
    }
    // GameSelectAll 的 options 由 InDevProjects/index.tsx 的 fetchProjectData 生成，
    // 每项包含 { name: project, unified_id, ... }，按 project_id 反查 unified_id
    const matched = (studioList || []).find((item: any) => String(item?.unified_id) === String(project.project_id));
    const targetProjectId = matched?.unified_id;
    if (!targetProjectId) {
      return;
    }

    // 仅在 InDevProjects 子页（subtitle=projects）下才更新 URL，避免误跳其他子页
    const params = new URLSearchParams(location.search);
    if (params.get('subtitle') !== 'projects') {
      return;
    }
    const btnPoint = createBtnPoint(
      InDevProjectsTab,
      'inDevProjectsAllProjectRedirectClick',
      `Project Redirect - ${project?.game_title || '-'} (ID: ${targetProjectId})`,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    params.set('project_id', String(targetProjectId));
    history.push(`/strategic/home?${params.toString()}`);
  };

  // 拖拽排序: 完成后重排可视 projects, 由父级 hook 同步底层源并生成提交格式
  const handleProjectDragEnd = (result: any) => {
    if (!result?.destination) {
      return;
    }
    const sourceIndex = result?.source?.index;
    const destIndex = result?.destination?.index;
    if (sourceIndex === destIndex) {
      return;
    }
    const reordered = Array.from(projects || []);
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, moved);
    handleProjectsReorder?.(reordered);
  };

  return (
    <>
      {/* Overview cards (Miniclip + Live-Ops) */}
      <div className="dbs-lto__overview dbs-home-studio-card">
        <OverviewCard key={TYPES.mc} type={TYPES.mc} projects={projectListAll} />
        <OverviewCard key={TYPES.lo} type={TYPES.lo} projects={projectListAll} />
      </div>
      {/* Arrow connector from active card to project list */}
      <CardCommon
        className={classnames('dbs-lto__project-list Inactive', {
          'dbs-lto__project-list--editing': isEditing,
        })}
      >
        {(projects?.length ?? 0) === 0 ? (
          <NoData style={{ height: '300px' }} text={'No Data'} />
        ) : (
          <rbd.DragDropContext onDragEnd={handleProjectDragEnd}>
            <rbd.Droppable droppableId="live-titles-projects">
              {(droppableProvided: any) => (
                <div
                  className="dbs-idp-project-rows"
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {projects?.map((project: any, index: number) => {
                    const draggableId = String(project?.project_id ?? `project-${index}`);
                    return (
                      <rbd.Draggable
                        key={draggableId}
                        draggableId={draggableId}
                        index={index}
                        isDragDisabled={!isEditing}
                      >
                        {(draggableProvided: any, snapshot: any) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            className={classnames('dbs-idp-project-row-draggable', {
                              'is-dragging': snapshot?.isDragging,
                            })}
                          >
                            <ProjectRow
                              project={project}
                              isEditing={isEditing}
                              onIconClick={handleProjectIconClick}
                              dragHandleProps={draggableProvided.dragHandleProps}
                              selectedMonth={selectedMonth}
                            />
                          </div>
                        )}
                      </rbd.Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </div>
              )}
            </rbd.Droppable>
          </rbd.DragDropContext>
        )}
      </CardCommon>
    </>
  );
};

export default InDevProjectsOverview;
